const READ_ONLY_VERIFICATION_TYPES = ['action_source_app_ssv']

export const isVerificationTypeEditable = (verificationType) =>
  !READ_ONLY_VERIFICATION_TYPES.includes(verificationType.code_key)

const BANNER_TYPE_LABELS = {
  action_source_admin: 'Manual Verification',
  action_source_app_ssv: 'SSV',
}
const SHORT_TYPE_LABELS = {
  action_source_admin: 'Manual',
  action_source_app_ssv: 'SSV',
}

export const fieldKey = (typeId, portfolioId) => `t${typeId}_p${portfolioId}`

export const providerField = (provider) =>
  provider.code_key.replace(/^provider_/, '')

const normalize = (value) => String(value ?? '').trim()

const parsePercent = (value) => {
  const text = normalize(value)
  if (!/^\d{1,3}$/.test(text)) return null
  const number = Number(text)
  return number <= 100 ? number : null
}

export const buildFormValues = (grid) => {
  const fieldsByProviderId = Object.fromEntries(
    grid.providers.map((provider) => [provider.id, providerField(provider)])
  )
  const values = {}
  grid.splits.forEach((split) => {
    const key = fieldKey(
      split.action_source_dropdown_option_id,
      split.portfolio_id
    )
    values[key] = {
      ...values[key],
      [fieldsByProviderId[split.provider_dropdown_option_id]]: String(
        split.percentage
      ),
    }
  })
  return values
}

export const isCellEdited = (originalValues, values, key, field) => {
  const current = values?.[key]?.[field]
  const original = originalValues?.[key]?.[field]
  const currentPercent = parsePercent(current)
  const originalPercent = parsePercent(original)
  if (currentPercent !== null && originalPercent !== null) {
    return currentPercent !== originalPercent
  }
  return normalize(current) !== normalize(original)
}

export const getCellTotal = (cell) =>
  Object.values(cell ?? {}).reduce(
    (total, value) => total + (Number(value) || 0),
    0
  )

export const isCellValid = (cell) => {
  const percents = Object.values(cell ?? {}).map(parsePercent)
  return (
    percents.length > 0 &&
    percents.every((percent) => percent !== null) &&
    percents.reduce((total, percent) => total + percent, 0) === 100
  )
}

const eachCell = (grid, callback) => {
  grid.verification_types.forEach((type) => {
    grid.portfolios.forEach((portfolio) => {
      callback(type, portfolio, fieldKey(type.id, portfolio.id))
    })
  })
}

// Read-only types are skipped: the user can't fix them, so they mustn't block saving the editable ones.
export const findInvalidCells = (grid, values) => {
  const invalid = []
  eachCell(grid, (type, portfolio, key) => {
    if (!isVerificationTypeEditable(type)) return
    if (!isCellValid(values?.[key])) {
      invalid.push({
        key,
        typeCodeKey: type.code_key,
        portfolioName: portfolio.name,
        total: getCellTotal(values?.[key]),
      })
    }
  })
  return invalid
}

export const formatInvalidCells = (invalidCells) =>
  invalidCells
    .map(
      (cell) =>
        `${BANNER_TYPE_LABELS[cell.typeCodeKey] ?? cell.typeCodeKey} - ${
          cell.portfolioName
        } (${cell.total}%)`
    )
    .join(', ')

export const buildChangeRows = (grid, originalValues, values) => {
  const rows = []
  eachCell(grid, (type, portfolio, key) => {
    grid.providers.forEach((provider) => {
      const field = providerField(provider)
      if (isCellEdited(originalValues, values, key, field)) {
        rows.push({
          key: `${key}-${field}`,
          typeLabel: SHORT_TYPE_LABELS[type.code_key] ?? type.name,
          portfolioName: portfolio.name,
          providerLabel: provider.name,
          before: normalize(originalValues?.[key]?.[field]),
          after: normalize(values?.[key]?.[field]),
        })
      }
    })
  })
  return rows
}

// The API requires every provider of an edited portfolio, so it can check the total is 100.
export const buildPayload = (grid, originalValues, values) => {
  const payload = []
  const fields = grid.providers.map(providerField)
  eachCell(grid, (type, portfolio, key) => {
    const isEdited = fields.some((field) => {
      return isCellEdited(originalValues, values, key, field)
    })
    if (!isEdited) return
    grid.providers.forEach((provider) => {
      payload.push({
        portfolio_id: portfolio.id,
        action_source_dropdown_option_id: type.id,
        provider_dropdown_option_id: provider.id,
        percentage: Number(normalize(values[key][providerField(provider)])),
      })
    })
  })
  return payload
}
